import React, {useEffect, useState} from 'react';
import {
  IFdOperatingCost,
  IGenericType,
  ISupplierUserProfile,
} from 'constants/interfaces';
import {
  reduxToggleSidePanel,
  reduxSetSidePanelEditState,
} from 'appredux/actions';
import {
  apiGetFdOperatingExpenses,
  apiUpsertFdOperatingCost,
} from 'constants/Api';
import {Box, Button} from '@material-ui/core';
import {
  FdOperatingCostForm,
  Submission,
} from 'components/applications/FdOperatingCostForm';
import {Skeleton} from '@material-ui/lab';
import CheckIcon from 'components/CheckIcon/CheckIcon';
import {Route} from 'react-router-dom';

const AddOrUpdateFdOperatingCost = (props: {
  user: ISupplierUserProfile;
  operatingCost?: IFdOperatingCost;
  applicationId: number;
}) => {
  const [expenses, setExpenses] = useState<IGenericType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSuccessfullyUpdated, setHasSuccessfullyUpdated] = useState(false);

  const isAdding = !props.operatingCost?.id;

  useEffect(() => {
    // Signal edit started
    reduxSetSidePanelEditState('editing');
    apiGetFdOperatingExpenses()
      .then((expenses) => setExpenses(expenses))
      .catch((err) => {
        alert('Failed to load expenses. Please try again later.');
        console.error('failed to load expenses', err);
      });

    return () => {
      // reset when panel unmounts if not saved
      reduxSetSidePanelEditState('idle');
    };
  }, []);

  const handleCancel = () => {
    reduxToggleSidePanel(false, null);
  };

  const handleSubmit = (submission: Submission) => {
    setIsLoading(true);

    const monthlyCosts = JSON.stringify({
      frequency: submission.frequency,
      total: submission.total,
      months:
        (submission.months || []).length > 1 ? submission.months : undefined,
      month:
        (submission.months || []).length === 1
          ? submission.months![0]
          : undefined,
      quarters: (submission.quarters || []).length
        ? submission.quarters
        : undefined,
    });

    apiUpsertFdOperatingCost({
      id: submission.id,
      applicationId: props.applicationId,
      expenseId: submission.expenseId,
      year: submission.year,
      monthlyCosts,
      notes: submission.notes ?? '',
    })
      .then(() => {
        setHasSuccessfullyUpdated(true);
        // Immediately signal that a save has occurred to trigger data refresh
        reduxSetSidePanelEditState('saved');
      })
      .catch((err) => {
        alert(
          'Failed to update operating cost at the moment. Please try again later',
        );
        console.error('Failed to update operating cost', err);
      })
      .finally(() => setIsLoading(false));
  };

  const canWrite = true;

  if (hasSuccessfullyUpdated) {
    return (
      <Route
        render={() => (
          <div className="side-panel-success-container">
            <div className="mid-section">
              <div className="success-gif">
                <CheckIcon />
              </div>
              <div className="success-msg">
                {isAdding
                  ? 'Operating Expense added successfully'
                  : 'Operating Expense updated successfully'}
              </div>
            </div>
            <div className="btn-container">
              <Button
                className="primary-btn"
                type="submit"
                variant="outlined"
                color="primary"
                disabled={isLoading}
                onClick={() => reduxToggleSidePanel(false, null)}
              >
                Done
              </Button>
            </div>
          </div>
        )}
      />
    );
  }
  // Close panel after successful update
  useEffect(() => {
    if (hasSuccessfullyUpdated) {
      // Close the side panel after a short delay to allow data refresh
      setTimeout(() => {
        reduxToggleSidePanel(false, null);
      }, 100);
    }
  }, [hasSuccessfullyUpdated]);

  if (hasSuccessfullyUpdated) return null;

  if (!expenses.length) {
    return (
      <Box margin={2}>
        <Skeleton variant="text" height={100} />
      </Box>
    );
  }

  return (
    <FdOperatingCostForm
      operatingCost={props.operatingCost}
      allExpenses={expenses}
      isLoading={isLoading}
      submitButtonTitle={isAdding ? 'Add' : 'Update'}
      onSubmit={handleSubmit}
      cancelButtonTitle="Cancel"
      onCancel={handleCancel}
      readonly={!canWrite}
    />
  );
};

export default AddOrUpdateFdOperatingCost;
